import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Video,
  Users,
  Shield,
  Monitor,
  MessageSquare,
  Lock,
  Mic,
  Camera,
  Calendar,
  FileText,
  PenTool,
  Hand,
  Smile,
  UserCheck,
  Sparkles,
  Radio,
  Clock,
  Globe,
  ArrowRight,
  Check,
  Zap,
  Heart,
} from 'lucide-react';
import { authService } from '../services/auth.service.ts';
import { FREE_MEETING_MAX_DURATION_MINUTES } from '../utils/plan.ts';

function Features() {
  const [user, setUser] = useState(() => authService.getUser());

  useEffect(() => {
    const syncUser = () => setUser(authService.getUser());
    window.addEventListener('storage', syncUser);
    return () => window.removeEventListener('storage', syncUser);
  }, []);

  const isLoggedIn = !!user;

  const highlights = [
    { value: `${FREE_MEETING_MAX_DURATION_MINUTES} dk`, label: 'Ücretsiz toplantı süresi' },
    { value: '0₺', label: 'Başlamak için ödeme yok' },
    { value: '1 tık', label: 'Anında toplantı başlat' },
    { value: '%100', label: 'Türkçe arayüz' },
  ];

  const mainFeatures = [
    {
      icon: Video,
      title: 'Kristal Net Görüntü',
      badge: 'En çok sevilen',
      description:
        'Takımınızı sanki aynı odadaymış gibi hissettirin. HD kalite, akıcı görüntü ve otomatik bağlantı optimizasyonu ile kesintisiz toplantılar.',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Shield,
      title: 'Gizliliğiniz Bizim İşimiz',
      badge: 'Bankacılık seviyesi',
      description:
        'Sohbetleriniz ve özel mesajlarınız güçlü şifreleme ile korunur. Hassas görüşmelerinizi güvenle yapın — verileriniz sizde kalır.',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: UserCheck,
      title: 'Siz Karar Verin, Kim Girecek',
      badge: 'Profesyonel kontrol',
      description:
        'Toplantıya girmeden önce görüntünüzü kontrol edin. Bekleme odası ile istemediğiniz kimse içeri giremez; her katılımcıyı tek tıkla onaylayın.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Monitor,
      title: 'Sunumlarınızı Paylaşın',
      description:
        'Sunum, rapor veya ekranınızı saniyeler içinde paylaşın. Herkes aynı sayfada — karışıklık yok, verimlilik tam.',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: MessageSquare,
      title: 'Anlık İletişim',
      description:
        'Toplantı sırasında mesajlaşın, özel not gönderin, hiçbir detayı kaçırmayın. Modern ve kullanımı kolay sohbet deneyimi.',
      color: 'from-indigo-500 to-purple-500',
    },
    {
      icon: Calendar,
      title: 'Randevularınızı Kaçırmayın',
      description:
        'Anlık toplantı başlatın veya ileri tarihli planlayın. Google Takvim ile senkronize edin, ekibiniz her zaman hazır olsun.',
      color: 'from-violet-500 to-fuchsia-500',
    },
  ];

  const additionalFeatures = [
    {
      icon: Camera,
      title: 'Profesyonel Görünün',
      description: 'Evden çalışırken bile ofis ortamı hissi — arka plan efektleri',
    },
    {
      icon: Hand,
      title: 'Söz Sırası',
      description: 'El kaldırma ile düzenli ve saygılı toplantılar',
    },
    {
      icon: Smile,
      title: 'Eğlenceli Anlar',
      description: 'Emoji tepkileriyle toplantıya enerji katın',
    },
    {
      icon: Radio,
      title: 'Hiçbir Şeyi Unutmayın',
      description: 'Toplantıları kaydedin, istediğiniz zaman tekrar izleyin',
    },
    {
      icon: PenTool,
      title: 'Birlikte Üretin',
      description: 'Beyaz tahta ile fikirleri görselleştirin',
    },
    {
      icon: FileText,
      title: 'Notlarınız Hazır',
      description: 'Toplantı notlarını tek yerde toplayın',
    },
    {
      icon: Users,
      title: 'Davet Kolaylığı',
      description: 'Misafirler hesap açmadan link ile katılsın',
    },
    {
      icon: Mic,
      title: 'Tam Kontrol',
      description: 'Toplantı sahibi olarak oturumu yönetin',
    },
    {
      icon: Sparkles,
      title: 'Akıllı Bağlantı',
      description: 'İnternet yavaşsa bile en iyi kaliteyi otomatik seçer',
    },
    {
      icon: Lock,
      title: 'Güvenli Aktarım',
      description: 'Görüntü ve sesiniz şifreli kanaldan iletilir',
    },
    {
      icon: Globe,
      title: 'Türkiye\'den, Türkiye\'ye',
      description: 'Türkçe arayüz, KVKK uyumu ve yerel destek',
    },
    {
      icon: Clock,
      title: 'Esnek Süreler',
      description: `Ücretsiz ${FREE_MEETING_MAX_DURATION_MINUTES} dk; Pro ile sınırsız toplantı`,
    },
  ];

  const whyUs = [
    {
      icon: Heart,
      title: 'Kullanımı Çok Kolay',
      text: 'Kurulum yok, indirme yok. Tarayıcınızı açın, toplantıya başlayın. Herkes anında adapte olur.',
    },
    {
      icon: Zap,
      title: 'Hızlı Başlangıç',
      text: 'Tek tıkla anlık toplantı oluşturun, linki paylaşın. Dakikalar içinde ekibiniz bir arada.',
    },
    {
      icon: Shield,
      title: 'Güvenilir Altyapı',
      text: 'Kurumsal düzeyde güvenlik ve Türkiye\'de barındırılan altyapı ile içiniz rahat olsun.',
    },
  ];

  const comparisonRows = [
    { feature: 'Ücretsiz toplantı süresi', tbk: `${FREE_MEETING_MAX_DURATION_MINUTES} dakika`, other: '40 dakika' },
    { feature: 'Kredi kartı zorunluluğu', tbk: 'Yok', other: 'Var' },
    { feature: 'Şifreli sohbet', tbk: '✓', other: 'Kısıtlı' },
    { feature: 'Türkçe arayüz & destek', tbk: '✓', other: '✗' },
    { feature: 'KVKK uyumu', tbk: '✓', other: '✗' },
    { feature: 'Veriler Türkiye\'de', tbk: '✓', other: '✗' },
    { feature: 'Bekleme odası', tbk: '✓', other: 'Ücretli planda' },
    { feature: 'Google Takvim', tbk: '✓', other: '✓' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Hero */}
      <section className="pt-28 pb-12 px-6 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl -top-48 -left-48" />
          <div className="absolute w-96 h-96 bg-pink-500/20 rounded-full blur-3xl -bottom-48 -right-48" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="text-purple-300 text-sm font-semibold uppercase tracking-wider mb-3">
            İşinizi kolaylaştıran platform
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Toplantılarınızı{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              bir üst seviyeye
            </span>{' '}
            taşıyın
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            Uzak ekiplerden müşteri görüşmelerine, eğitimlerden beyin fırtınalarına — TBKMeet ile
            her toplantı daha verimli, daha güvenli ve daha keyifli.
          </p>
          {!isLoggedIn && (
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold transition transform hover:scale-105 hover:shadow-2xl"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-5 h-5" />
            </Link>
          )}
        </div>
      </section>

      {/* Stats strip */}
      <section className="pb-16 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {highlights.map((item) => (
            <div
              key={item.label}
              className="text-center p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm"
            >
              <div className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                {item.value}
              </div>
              <div className="text-gray-400 text-xs sm:text-sm mt-1">{item.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Main Features */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
            Neler Sunuyoruz?
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-xl mx-auto">
            İhtiyacınız olan her şey, karmaşık kurulumlar olmadan
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mainFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 hover:bg-white/10 hover:border-purple-500/30 hover:-translate-y-1 transition-all duration-300 group"
                >
                  {'badge' in feature && feature.badge && (
                    <span className="inline-block text-[10px] font-semibold uppercase tracking-wide text-purple-300 bg-purple-500/15 border border-purple-500/25 px-2 py-0.5 rounded-full mb-3">
                      {feature.badge}
                    </span>
                  )}
                  <div
                    className={`w-14 h-14 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why choose us - 3 pillars */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {whyUs.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="text-center p-6 rounded-2xl bg-gradient-to-b from-white/10 to-transparent border border-white/10"
              >
                <div className="w-12 h-12 mx-auto mb-4 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <Icon className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Security - customer friendly */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="relative overflow-hidden bg-gradient-to-r from-green-900/30 to-emerald-900/20 rounded-2xl border border-green-500/20 p-8 md:p-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-16 h-16 flex-shrink-0 bg-green-500/20 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-green-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Verileriniz Güvende
                </h2>
                <p className="text-gray-300 leading-relaxed">
                  Müşteri görüşmeleriniz, ekip toplantılarınız ve özel mesajlarınız güçlü
                  şifreleme ile korunur. TBKMeet, gizliliğinizi ciddiye alır — siz toplantınıza
                  odaklanın, gerisini biz halledelim.
                </p>
              </div>
            </div>
            <ul className="relative mt-6 grid sm:grid-cols-2 gap-3">
              {[
                'Şifreli sohbet ve özel mesajlar',
                'Güvenli görüntü ve ses aktarımı',
                'KVKK uyumlu veri politikası',
                'Türkiye\'de barındırılan altyapı',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2 text-gray-200 text-sm">
                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-16 px-6 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
            Ve Dahası...
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-xl mx-auto">
            Toplantı deneyiminizi zenginleştiren onlarca detay
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {additionalFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 hover:bg-white/10 hover:border-purple-500/20 transition"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-purple-400" />
                    </div>
                    <h3 className="text-white font-semibold text-sm">{feature.title}</h3>
                  </div>
                  <p className="text-gray-400 text-xs leading-relaxed pl-11">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
            Neden TBKMeet Tercih Ediliyor?
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Rakiplerle yan yana koyduğumuzda fark ortada
          </p>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th className="px-4 sm:px-6 py-4 text-left text-white font-semibold">
                      Özellik
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-center text-purple-400 font-semibold">
                      TBKMeet
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-center text-gray-400 font-semibold">
                      Diğer Platformlar
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr
                      key={row.feature}
                      className="border-b border-white/5 hover:bg-white/5 transition"
                    >
                      <td className="px-4 sm:px-6 py-4 text-gray-300">{row.feature}</td>
                      <td className="px-4 sm:px-6 py-4 text-center text-green-400 font-medium">
                        {row.tbk}
                      </td>
                      <td className="px-4 sm:px-6 py-4 text-center text-gray-500">{row.other}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-3xl border border-purple-500/20 p-10 md:p-14">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {isLoggedIn ? 'Planınızı yükseltin' : 'İlk toplantınızı bugün yapın'}
            </h2>
            <p className="text-gray-300 mb-8 max-w-lg mx-auto">
              {isLoggedIn
                ? 'Daha uzun toplantılar, daha fazla katılımcı ve premium özellikler sizi bekliyor.'
                : `Kredi kartı gerekmez. ${FREE_MEETING_MAX_DURATION_MINUTES} dakikaya kadar ücretsiz toplantı — hemen başlayın, farkı kendiniz görün.`}
            </p>
            {isLoggedIn ? (
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-lg transition transform hover:scale-105 hover:shadow-2xl"
              >
                Size uygun planları görün
                <ArrowRight className="w-5 h-5" />
              </Link>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold text-lg transition transform hover:scale-105 hover:shadow-2xl"
                >
                  Ücretsiz Hesap Oluştur
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-10 py-4 bg-white/10 text-white rounded-xl font-semibold text-lg border border-white/20 hover:bg-white/20 transition"
                >
                  Fiyatları İncele
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Features;
